/**
 * ChannelList Component
 * Displays list of messaging channels with unread counts
 */

import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Hash,
  Lock,
  Plus,
  Search,
  Users,
  Volume2,
  ChevronDown,
  ChevronRight,
  Settings,
  MoreHorizontal,
  Archive,
  ArchiveRestore,
  LogOut,
  Trash2,
  Pin,
  PinOff,
  ArrowUp,
  ArrowDown,
  Zap,
  Mail,
  MailOpen,
} from 'lucide-react';
import { messagingApi, Channel, ChannelType } from '@/services/messagingApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { InlineNotice, useInlineNotice } from './InlineNotice';
import {
  DeleteChannelDialog,
  getChannelPermissions,
  useChannelLifecycle,
  useIsOrgAdmin,
} from './channelActions';
import { sortedPins, useChannelPins } from './channelPins';
import { SwipeRow } from './swipe/SwipeRow';
import { swipeSides, useSwipeSettings } from './swipe/swipeSettings';
import { useConversationReadToggle } from './swipe/useConversationReadToggle';
import { messagePreviewText } from './linkify';
import { initialsOf, shortTime } from './inbox/memberInbox';

interface ChannelListProps {
  selectedChannelId?: number;
  onSelectChannel: (channel: Channel) => void;
  onCreateChannel?: () => void;
  onCreateDM?: () => void;
  /** A channel was deleted / archived / left from its row menu. */
  onChannelRemoved?: (channelId: number) => void;
  /**
   * TRUS-709: leave member conversations out — they live in the member Inbox
   * (MemberInboxList) when its endpoint is available.
   */
  excludeMemberDms?: boolean;
}

export function ChannelList({
  selectedChannelId,
  onSelectChannel,
  onCreateChannel,
  onCreateDM,
  onChannelRemoved,
  excludeMemberDms = false,
}: ChannelListProps) {
  const { t } = useAppTranslation();
  const isOrgAdmin = useIsOrgAdmin();
  const pinActions = useChannelPins();
  const readToggle = useConversationReadToggle();
  const [search, setSearch] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    pinned: true,
    channels: true,
    directMessages: true,
  });
  // Drag-to-reorder within Pinned (Move up/down in the row menu is the keyboard path).
  const [dragId, setDragId] = useState<number | null>(null);

  const { data: allChannels = [], isLoading, refetch } = useQuery({
    queryKey: ['messaging-channels', search],
    queryFn: () => messagingApi.listChannels({ search: search || undefined }),
    refetchInterval: 30000, // Refetch every 30 seconds for unread counts
  });
  const channels = excludeMemberDms ? allChannels.filter((c) => c.dm_member_id == null) : allChannels;

  // TRUS-638: pinned channels AND DMs get their own section at the top, in pin
  // order; the sections below leave them out.
  const pinned = sortedPins(channels);
  const unpinned = channels.filter((c) => !c.is_pinned);

  // Separate channels by type
  const publicChannels = unpinned.filter(
    (c) => c.channel_type === 'public' && !c.is_dm
  );
  const privateChannels = unpinned.filter(
    (c) => c.channel_type === 'private' && !c.is_dm
  );
  const directMessages = unpinned.filter((c) => c.is_dm);
  const announcements = unpinned.filter(
    (c) => c.channel_type === 'announcement'
  );

  const toggleSection = (section: 'pinned' | 'channels' | 'directMessages') => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const getTotalUnread = () => {
    return channels.reduce((sum, c) => sum + (c.unread_count || 0), 0);
  };

  const renderItem = (channel: Channel, pinIndex = -1) => (
    <ChannelItem
      key={channel.id}
      channel={channel}
      isSelected={selectedChannelId === channel.id}
      onClick={() => onSelectChannel(channel)}
      isOrgAdmin={isOrgAdmin}
      onRemoved={() => onChannelRemoved?.(channel.id)}
      isPinned={!!channel.is_pinned}
      onPin={() => pinActions.pin.mutate(channel.id)}
      onUnpin={() => pinActions.unpin.mutate(channel.id)}
      onToggleRead={() => readToggle.mutate({ channelId: channel.id, unread: (channel.unread_count || 0) > 0 })}
      onMoveUp={pinIndex > 0 ? () => pinActions.move(pinned, channel.id, -1) : undefined}
      onMoveDown={
        pinIndex >= 0 && pinIndex < pinned.length - 1
          ? () => pinActions.move(pinned, channel.id, 1)
          : undefined
      }
      dragProps={
        pinIndex >= 0
          ? {
              draggable: true,
              onDragStart: (e: React.DragEvent) => {
                setDragId(channel.id);
                e.dataTransfer?.setData('text/plain', String(channel.id));
                if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
              },
              onDragOver: (e: React.DragEvent) => {
                if (dragId != null) e.preventDefault();
              },
              onDrop: (e: React.DragEvent) => {
                e.preventDefault();
                if (dragId != null) pinActions.moveTo(pinned, dragId, channel.id);
                setDragId(null);
              },
              onDragEnd: () => setDragId(null),
            }
          : undefined
      }
      isDragging={dragId === channel.id}
    />
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">{t('messaging.list.title', 'Messages')}</h2>
          {getTotalUnread() > 0 && (
            <Badge variant="danger">{getTotalUnread()}</Badge>
          )}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('messaging.list.searchPlaceholder', 'Search channels...')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Channel List */}
      <div className="flex-1 overflow-y-auto">
        {/* Pinned (TRUS-638) — channels and DMs, in the caller's pin order */}
        {pinned.length > 0 && (
          <div className="py-2" data-testid="pinned-section">
            <button
              onClick={() => toggleSection('pinned')}
              className="w-full px-4 py-1 flex items-center justify-between text-xs font-semibold text-muted-foreground uppercase hover:text-foreground"
              aria-expanded={expandedSections.pinned}
            >
              <span className="flex items-center gap-1">
                {expandedSections.pinned ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
                <Pin className="h-3 w-3" />
                {t('messaging.pins.section', 'Pinned')}
              </span>
            </button>
            {expandedSections.pinned && (
              <div role="list" aria-label={t('messaging.pins.section', 'Pinned')}>
                {pinned.map((channel, i) => renderItem(channel, i))}
              </div>
            )}
          </div>
        )}

        {/* Announcements */}
        {announcements.length > 0 && (
          <div className="py-2">
            <div className="px-4 py-1 text-xs font-semibold text-muted-foreground uppercase">
              {t('messaging.list.announcements', 'Announcements')}
            </div>
            {announcements.map((channel) => renderItem(channel))}
          </div>
        )}

        {/* Channels Section */}
        <div className="py-2">
          <button
            onClick={() => toggleSection('channels')}
            className="w-full px-4 py-1 flex items-center justify-between text-xs font-semibold text-muted-foreground uppercase hover:text-foreground"
          >
            <span className="flex items-center gap-1">
              {expandedSections.channels ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              {t('messaging.list.channels', 'Channels')}
            </span>
            {onCreateChannel && (
              <Plus
                className="h-4 w-4 hover:text-primary cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  onCreateChannel();
                }}
              />
            )}
          </button>
          {expandedSections.channels && (
            <>
              {publicChannels.map((channel) => renderItem(channel))}
              {privateChannels.map((channel) => renderItem(channel))}
              {publicChannels.length === 0 && privateChannels.length === 0 && (
                <div className="px-4 py-2 text-sm text-muted-foreground">
                  {t('messaging.list.noChannels', 'No channels yet')}
                </div>
              )}
            </>
          )}
        </div>

        {/* Direct Messages Section */}
        <div className="py-2">
          <button
            onClick={() => toggleSection('directMessages')}
            className="w-full px-4 py-1 flex items-center justify-between text-xs font-semibold text-muted-foreground uppercase hover:text-foreground"
          >
            <span className="flex items-center gap-1">
              {expandedSections.directMessages ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              {t('messaging.list.directMessages', 'Direct Messages')}
            </span>
            {onCreateDM && (
              <Plus
                className="h-4 w-4 hover:text-primary cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  onCreateDM();
                }}
              />
            )}
          </button>
          {expandedSections.directMessages && (
            <>
              {directMessages.map((channel) => renderItem(channel))}
              {directMessages.length === 0 && (
                <div className="px-4 py-2 text-sm text-muted-foreground">
                  {t('messaging.list.noConversations', 'No conversations yet')}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Staff DM: the other person's initials. Group DM (the list labels it
 * "Ana Ruiz, Ben Cho"): the first two, overlapped. Channel: its type icon.
 */
function ChannelAvatar({ channel, label }: { channel: Channel; label: string }) {
  const { t } = useAppTranslation();
  const circle =
    'flex items-center justify-center rounded-full bg-indigo-100 font-semibold text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-200';
  if (channel.is_dm) {
    const people = label.split(',').map((p) => p.trim()).filter(Boolean);
    if (people.length > 1) {
      return (
        <span className="relative mt-0.5 h-8 w-8 flex-shrink-0" data-testid={`channel-avatar-group-${channel.id}`} aria-hidden="true">
          <span className={cn(circle, 'absolute left-0 top-0 h-5 w-5 text-[9px]')}>{initialsOf(people[0])}</span>
          <span className={cn(circle, 'absolute bottom-0 right-0 h-5 w-5 border-2 border-slate-50 text-[9px] dark:border-slate-900')}>
            {people.length > 2
              ? t('messaging.inbox.moreCount', '+{{count}}', { count: people.length - 1 })
              : initialsOf(people[1])}
          </span>
        </span>
      );
    }
    return (
      <span className={cn(circle, 'mt-0.5 h-8 w-8 flex-shrink-0 text-xs')} data-testid={`channel-avatar-dm-${channel.id}`} aria-hidden="true">
        {initialsOf(label)}
      </span>
    );
  }
  const Icon = channel.channel_type === 'private' ? Lock : channel.channel_type === 'announcement' ? Volume2 : Hash;
  return (
    <span
      className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
      data-testid={`channel-avatar-channel-${channel.id}`}
      aria-hidden="true"
    >
      <Icon className="h-4 w-4" />
    </span>
  );
}

interface ChannelItemProps {
  channel: Channel;
  isSelected: boolean;
  onClick: () => void;
  isOrgAdmin: boolean;
  onRemoved?: () => void;
  isPinned: boolean;
  onPin: () => void;
  onUnpin: () => void;
  /** TRUS-759: mark read when unread, else mark unread. */
  onToggleRead?: () => void;
  /** Set only for pinned rows that can move (keyboard alternative to dragging). */
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  dragProps?: React.HTMLAttributes<HTMLDivElement> & { draggable?: boolean };
  isDragging?: boolean;
}

function ChannelItem({
  channel,
  isSelected,
  onClick,
  isOrgAdmin,
  onRemoved,
  isPinned,
  onPin,
  onUnpin,
  onToggleRead,
  onMoveUp,
  onMoveDown,
  dragProps,
  isDragging,
}: ChannelItemProps) {
  const { t } = useAppTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const touchOpen = useRef(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const hasUnread = (channel.unread_count || 0) > 0;
  const hasMentions = (channel.mention_count || 0) > 0;
  // Red unread badge when the list reports unread urgent messages.
  const hasUrgent = hasUnread && (channel.urgent_unread_count || 0) > 0;
  // The list only has the summary Channel (no members), so owner/admin is
  // unknown here: delete shows for org admins, archive for everyone (server gates).
  const perms = getChannelPermissions(channel, isOrgAdmin);
  // Errors are surfaced on the row; success is a toast (from the lifecycle hook).
  const { notice, show: showNotice, clear: clearNotice } = useInlineNotice();
  const lifecycle = useChannelLifecycle(
    channel,
    () => {
      setConfirmDelete(false);
      onRemoved?.();
    },
    showNotice,
  );
  const hasLifecycle = perms.canArchive || perms.canLeave || perms.canDelete;
  // Pin / Unpin is offered on every row, so every row has a menu.
  const label = channel.display_name || channel.name;
  const preview = messagePreviewText(channel.last_message_preview);
  const readLabel = hasUnread
    ? t('messaging.readState.markRead', 'Mark read')
    : t('messaging.readState.markUnread', 'Mark unread');
  const pinLabel = isPinned ? t('messaging.pins.unpin', 'Unpin') : t('messaging.pins.pin', 'Pin to top');
  // TRUS-761: which side pins and which marks read / unread is the user's setting.
  const swipeSettings = useSwipeSettings();
  const sides = swipeSides(swipeSettings.conversations, {
    pin: { label: pinLabel, icon: isPinned ? PinOff : Pin, tone: 'pin' as const, onCommit: isPinned ? onUnpin : onPin },
    read: onToggleRead
      ? { label: readLabel, icon: hasUnread ? MailOpen : Mail, tone: 'read' as const, onCommit: onToggleRead }
      : undefined,
  });

  return (
    <div
      className={cn('group relative', isDragging && 'opacity-50')}
      data-testid={`channel-row-${channel.id}`}
      {...dragProps}
      onContextMenu={(e) => {
        e.preventDefault();
        setMenuOpen(true);
      }}
    >
      {/* TRUS-759/761: swipe (touch or trackpad) to pin or mark read / unread, per the
          user's swipe settings. A mouse drag never swipes, so drag-to-reorder of pinned rows is untouched. */}
      <SwipeRow
        right={sides.right}
        left={sides.left}
        contentClassName="bg-slate-50 dark:bg-slate-900"
        outerProps={{ 'data-testid': `channel-swipe-${channel.id}` }}
      >
      {/* Two lines, like the Members rows: avatar · name + time · preview + unread. */}
      <button
        onClick={onClick}
        aria-current={isSelected || undefined}
        className={cn(
          'flex w-full gap-2.5 border-b px-3 py-2.5 text-left transition-colors hover:bg-slate-100 dark:hover:bg-slate-800',
          '[@media(hover:none)]:pr-14',
          isSelected && 'bg-slate-200 dark:bg-slate-700',
        )}
      >
        <ChannelAvatar channel={channel} label={label} />
        <span className="min-w-0 flex-1">
          <span className="flex items-center justify-between gap-2">
            <span className="flex min-w-0 items-center gap-1">
              <span
                className={cn('truncate text-sm', hasUnread ? 'font-semibold' : 'font-medium')}
                data-testid={`channel-name-${channel.id}`}
              >
                {label}
              </span>
              {/* TRUS-684: the channel receives WeaveIn system events. */}
              {channel.has_event_subscription && !channel.is_dm && (
                <span
                  className="flex-shrink-0 text-muted-foreground"
                  title={t('messaging.systemEvents.connected', 'Connected to events')}
                  data-testid={`channel-events-bolt-${channel.id}`}
                >
                  <Zap className="h-3.5 w-3.5" aria-hidden="true" />
                  <span className="sr-only">{t('messaging.systemEvents.connected', 'Connected to events')}</span>
                </span>
              )}
            </span>
            <span className="flex-shrink-0 text-[11px] text-muted-foreground" data-testid={`channel-time-${channel.id}`}>
              {shortTime(channel.last_message_at, t)}
            </span>
          </span>
          <span className="flex items-center gap-1.5">
            <span
              className={cn('min-w-0 flex-1 truncate text-xs', hasUnread ? 'text-foreground' : 'text-muted-foreground')}
              data-testid={`channel-preview-${channel.id}`}
            >
              {preview || t('messaging.list.noMessagesYet', 'No messages yet')}
            </span>
            {hasMentions ? (
              <Badge variant="danger" className="min-w-[20px] h-5 text-xs">
                {channel.mention_count}
              </Badge>
            ) : hasUnread && hasUrgent ? (
              <span
                className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-600 px-1.5 text-xs font-semibold text-white"
                data-testid="channel-urgent-badge"
                title={t('messaging.urgent.unreadTitle', 'Unread urgent messages')}
              >
                {channel.unread_count > 99 ? '99+' : channel.unread_count}
              </span>
            ) : hasUnread ? (
              <span
                className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground"
                data-testid="channel-unread-badge"
              >
                {channel.unread_count > 99 ? '99+' : channel.unread_count}
              </span>
            ) : null}
          </span>
        </span>
      </button>

      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              // Over the row's time on hover, like the Members row's pin button. Touch
              // screens show it all the time, so there it sits in its own column.
              'absolute right-2 top-1.5 rounded bg-slate-100 p-1 text-muted-foreground hover:bg-slate-200 hover:text-foreground dark:bg-slate-800 dark:hover:bg-slate-700',
              '[@media(hover:none)]:top-1/2 [@media(hover:none)]:-translate-y-1/2 [@media(hover:none)]:bg-transparent',
              // Touch screens have no hover: keep the menu reachable there.
              'opacity-0 focus:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100',
              menuOpen && 'opacity-100'
            )}
            title={t('messaging.channel.moreActions', 'Channel actions')}
            aria-label={t('messaging.pins.rowActions', 'Actions for {{name}}', { name: label })}
            // TRUS-759: Radix opens the menu on pointerdown, so a swipe that started
            // on this button also opened it. A finger or pen opens it on the tap's
            // click instead - and the click after a swipe never arrives (SwipeRow).
            onPointerDown={(e) => {
              touchOpen.current = e.pointerType === 'touch' || e.pointerType === 'pen';
              if (touchOpen.current) e.preventDefault();
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (touchOpen.current) setMenuOpen((open) => !open);
              touchOpen.current = false;
            }}
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        {/* The trigger moves with the swiped row; the menu content is portalled. */}
        <DropdownMenuContent align="end">
          {isPinned ? (
            <DropdownMenuItem onSelect={onUnpin}>
              <PinOff className="mr-2 h-4 w-4" />
              {t('messaging.pins.unpin', 'Unpin')}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onSelect={onPin}>
              <Pin className="mr-2 h-4 w-4" />
              {t('messaging.pins.pin', 'Pin to top')}
            </DropdownMenuItem>
          )}
          {onToggleRead && (
            <DropdownMenuItem onSelect={onToggleRead} data-testid={`channel-read-toggle-${channel.id}`}>
              {hasUnread ? <MailOpen className="mr-2 h-4 w-4" /> : <Mail className="mr-2 h-4 w-4" />}
              {readLabel}
            </DropdownMenuItem>
          )}
          {onMoveUp && (
            <DropdownMenuItem onSelect={onMoveUp}>
              <ArrowUp className="mr-2 h-4 w-4" />
              {t('messaging.pins.moveUp', 'Move up')}
            </DropdownMenuItem>
          )}
          {onMoveDown && (
            <DropdownMenuItem onSelect={onMoveDown}>
              <ArrowDown className="mr-2 h-4 w-4" />
              {t('messaging.pins.moveDown', 'Move down')}
            </DropdownMenuItem>
          )}
          {hasLifecycle && <DropdownMenuSeparator />}
          {perms.canArchive &&
            (channel.is_archived ? (
              <DropdownMenuItem onSelect={() => lifecycle.unarchive.mutate()}>
                <ArchiveRestore className="mr-2 h-4 w-4" />
                {t('messaging.channel.unarchive', 'Unarchive channel')}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onSelect={() => lifecycle.archive.mutate()}>
                <Archive className="mr-2 h-4 w-4" />
                {perms.isMemberDM
                  ? t('messaging.channel.archiveConversation', 'Archive conversation')
                  : t('messaging.channel.archive', 'Archive channel')}
              </DropdownMenuItem>
            ))}
          {perms.canLeave && (
            <DropdownMenuItem onSelect={() => lifecycle.leave.mutate()}>
              <LogOut className="mr-2 h-4 w-4" />
              {t('messaging.channel.leaveTitle', 'Leave channel')}
            </DropdownMenuItem>
          )}
          {perms.canDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => setConfirmDelete(true)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('messaging.channel.delete', 'Delete channel')}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      </SwipeRow>

      {notice && (
        <div className="px-2 pb-1">
          <InlineNotice notice={notice} onClose={clearNotice} className="text-xs" />
        </div>
      )}

      {perms.canDelete && (
        <DeleteChannelDialog
          open={confirmDelete}
          onOpenChange={(open) => {
            setConfirmDelete(open);
            if (!open) lifecycle.remove.reset();
          }}
          channelName={channel.display_name || channel.name}
          isPending={lifecycle.remove.isPending}
          error={lifecycle.remove.error?.message}
          onConfirm={() => lifecycle.remove.mutate()}
        />
      )}
    </div>
  );
}

export default ChannelList;
